import { useState } from "react";
import { ArrowRight, BookOpen, ChevronRight, Code2, FileText, Package, Rocket, Server, Terminal, Zap } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav, brandPalette } from "./components/brand-scaffold";
import { FaqSection, LinkClusterSection } from "./components/seo-support";
import { siteHeaderActions, siteNavItems } from "./content";
import { ROUTE_FAQS } from "./seo-content";

type DocSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  summary: string;
  articles: DocArticle[];
};

type DocArticle = {
  id: string;
  title: string;
  summary: string;
  tags?: string[];
  comingSoon?: boolean;
  href?: string;
};

const DOC_SECTIONS: DocSection[] = [
  {
    id: "quick-start",
    label: "快速开始",
    icon: <Rocket className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.green,
    summary: "5 分钟内跑通 Caller 或 Responder 任意一条最小闭环。",
    articles: [
      {
        id: "qs-caller",
        title: "Caller Quick Start · 5 min",
        summary: "Agent 应用方：装好 client → 启 supervisor → 发起一次真实 hotline 调用 → 解读 result_package。",
        tags: ["Caller", "推荐入口"],
        href: "/docs/quick-start-caller"
      },
      {
        id: "qs-responder",
        title: "Responder Quick Start · 5 min",
        summary: "能力供给方：启用 responder → 用示例 hotline 验证链路 → 写自己的 hotline → submit-review 上 Marketplace。",
        tags: ["Responder", "推荐入口"],
        href: "/docs/quick-start-responder"
      },
      {
        id: "local-dev",
        title: "本地开发环境（Local Mode 全量）",
        summary: "使用第四仓 source integration，在本地跑完整的 Platform + Client 堆栈，覆盖审批 / 计费 / Marketplace 全链路。",
        tags: ["开发", "Docker"]
      }
    ]
  },
  {
    id: "protocol",
    label: "协议规范",
    icon: <FileText className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.green,
    summary: "合约格式、结果签名、Token 模型和能力模板的权威定义。",
    articles: [
      {
        id: "contract-format",
        title: "合约格式（Contract Format）",
        summary: "task.input、task.output_schema、constraints、attachments 字段的完整定义。",
        tags: ["协议", "Schema"]
      },
      {
        id: "result-format",
        title: "结果包格式（Result Package）",
        summary: "result_version、output、error、signature 字段及 canonicalization 规则。",
        tags: ["协议", "签名"]
      },
      {
        id: "protocol-examples",
        title: "协议最小示例",
        summary: "把最小 request、成功 result_package、错误返回、registration payload 集中放到一个稳定页面里。",
        tags: ["协议", "示例", "证据页"],
        href: "/docs/protocol-examples"
      },
      {
        id: "protocol-architecture",
        title: "协议架构（Protocol Architecture）",
        summary: "把 Protocol / Client / Platform / Selfhost 以及 control plane / data plane 的关系单独讲清楚。",
        tags: ["架构", "控制面", "数据面"],
        href: "/docs/protocol-architecture"
      },
      {
        id: "token-model",
        title: "Token 与签名模型",
        summary: "JWT claims、introspect 流程、Ed25519 结果签名验证步骤。",
        tags: ["安全", "JWT"]
      },
      {
        id: "capability-templates",
        title: "能力模板（Capability Templates）",
        summary: "input_schema、output_schema、attachments.schema.json 和 README.md 的组织规范。",
        tags: ["模板", "Schema"]
      },
      {
        id: "file-attachments",
        title: "文件附件模型",
        summary: "input_attachments / output_attachments 的对称设计、file_roles 声明与 JSON 引用机制。",
        tags: ["附件", "文件"]
      }
    ]
  },
  {
    id: "client-sdk",
    label: "Client SDK",
    icon: <Code2 className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.blue,
    summary: "delexec-ops CLI 和 Caller/Responder Controller 的使用指南。",
    articles: [
      {
        id: "ops-cli",
        title: "delexec-ops CLI 参考",
        summary: "所有 CLI 子命令、全局参数和环境变量一览。",
        tags: ["CLI", "参考"]
      },
      {
        id: "caller-controller",
        title: "Caller Controller",
        summary: "通过 HTTP API 提交任务、查询状态、接收结果的完整流程。",
        tags: ["Caller", "HTTP API"]
      },
      {
        id: "responder-controller",
        title: "Responder Controller",
        summary: "注册 Hotline、接收任务、执行并返回签名结果的实现指南。",
        tags: ["Responder", "签名"]
      },
      {
        id: "transports",
        title: "传输层（Transports）",
        summary: "local、relay-http、email 三种传输适配器的配置与使用说明。",
        tags: ["传输", "配置"]
      }
    ]
  },
  {
    id: "platform-api",
    label: "Platform API",
    icon: <Server className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.purple,
    summary: "Platform API REST 接口的完整参考，包含 Marketplace、目录、Token 和管理接口。",
    articles: [
      {
        id: "marketplace-api",
        title: "Marketplace API",
        summary: "GET /marketplace/hotlines、GET /marketplace/hotlines/:id、GET /marketplace/hotlines/:id/template-bundle 详解。",
        tags: ["API", "Marketplace"]
      },
      {
        id: "catalog-api",
        title: "目录 API（Catalog API）",
        summary: "GET /v2/hotlines 目录查询，含筛选参数和响应字段说明。",
        tags: ["API", "目录"]
      },
      {
        id: "token-api",
        title: "Token API",
        summary: "POST /v1/tokens/task 签发 Task Token，POST /v1/tokens/introspect 在线校验。",
        tags: ["API", "Token", "安全"]
      },
      {
        id: "registration-api",
        title: "注册 API",
        summary: "POST /v1/responders/register Hotline 注册流程与字段说明（含新增的 summary、attachments、examples 等字段）。",
        tags: ["API", "Responder", "注册"]
      }
    ]
  },
  {
    id: "selfhost",
    label: "自托管部署",
    icon: <Package className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.yellow,
    summary: "使用 Docker Compose 在自有基础设施上部署完整平台堆栈。",
    articles: [
      {
        id: "deploy-platform",
        title: "部署 Platform 堆栈",
        summary: "Local Agent Loop、Selfhost Platform、Public Stack、Management Console 四条 profile 的启动、检查和安全边界。",
        tags: ["部署", "Docker", "安全"],
        href: "/docs/deployability-profiles"
      },
      {
        id: "deploy-caller",
        title: "部署 Caller Controller",
        summary: "deploy/caller/docker-compose.yml 环境变量说明与最小配置。",
        tags: ["部署", "Caller"]
      },
      {
        id: "deploy-responder",
        title: "部署 Responder Controller",
        summary: "deploy/responder/docker-compose.yml 环境变量、密钥管理与 Hotline 绑定。",
        tags: ["部署", "Responder"]
      },
      {
        id: "ops-guide",
        title: "运维操作指南",
        summary: "日志查看、健康检查、心跳监控与常见故障处理。",
        tags: ["运维", "监控"]
      }
    ]
  },
  {
    id: "hotline-authoring",
    label: "Hotline 开发",
    icon: <Zap className="h-6 w-6" strokeWidth={3} />,
    color: brandPalette.pink,
    summary: "从零设计并发布一个 Hotline，包含能力模板、schema 设计和 Marketplace 上架流程。",
    articles: [
      {
        id: "design-hotline",
        title: "设计 Hotline 能力",
        summary: "三层内容模型（发现层/评估层/使用层）的字段设计指南与 summary 写作要点。",
        tags: ["设计", "模板"],
        comingSoon: false
      },
      {
        id: "schema-design",
        title: "Schema 设计规范",
        summary: "input.schema.json / output.schema.json 的 JSON Schema 2020-12 规范与向后兼容策略。",
        tags: ["Schema", "规范"]
      },
      {
        id: "attachments-design",
        title: "文件附件声明",
        summary: "attachments.schema.json 的 file_roles 设计，输入/输出对称结构与 JSON 引用约定。",
        tags: ["附件", "设计"]
      },
      {
        id: "publish-hotline",
        title: "上架到 Marketplace",
        summary: "通过注册 API 提交 Hotline、完成 review_test 验证并等待上线审核的完整流程。",
        tags: ["上架", "Marketplace"]
      }
    ]
  }
];

function ArticleCard({ article, sectionColor }: { article: DocArticle; sectionColor: string }) {
  const inner = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-black uppercase leading-snug">{article.title}</h3>
          {article.comingSoon ? (
            <span className="border border-zinc-300 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.14em] text-zinc-500">即将上线</span>
          ) : null}
        </div>
        <p className="mt-2 text-xs font-bold leading-5 text-zinc-600">{article.summary}</p>
        {article.tags?.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {article.tags.map((tag) => (
              <span key={tag} className="border border-black/20 bg-zinc-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em]">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {!article.comingSoon ? (
        <ArrowRight
          className="mt-1 h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          strokeWidth={3}
          style={{ color: sectionColor }}
        />
      ) : null}
    </div>
  );

  const baseClass = `group border-2 border-black bg-white p-4 transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 ${
    article.comingSoon ? "opacity-70" : "cursor-pointer"
  }`;

  if (article.href && !article.comingSoon) {
    return (
      <a href={article.href} className={`${baseClass} block`}>
        {inner}
      </a>
    );
  }

  return <div className={baseClass}>{inner}</div>;
}

function SectionNav({ sections, activeId, onSelect }: { sections: DocSection[]; activeId: string; onSelect: (id: string) => void }) {
  return (
    <nav className="grid gap-1.5">
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() => onSelect(section.id)}
          className="flex items-center gap-3 border-2 p-3 text-left transition-colors"
          style={{
            borderColor: activeId === section.id ? section.color : "rgba(0,0,0,0.15)",
            backgroundColor: activeId === section.id ? `${section.color}26` : "#ffffff"
          }}
        >
          <span style={{ color: activeId === section.id ? section.color : "#71717a" }}>{section.icon}</span>
          <span className="text-sm font-black uppercase" style={{ color: activeId === section.id ? "#111111" : "#52525b" }}>
            {section.label}
          </span>
          <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-zinc-400" strokeWidth={2.5} />
        </button>
      ))}
    </nav>
  );
}

export default function Docs() {
  const [activeSectionId, setActiveSectionId] = useState(DOC_SECTIONS[0].id);
  const activeSection = DOC_SECTIONS.find((s) => s.id === activeSectionId) || DOC_SECTIONS[0];

  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <main className="relative z-10 w-full overflow-hidden">
        {/* Hero */}
        <section className="border-b-4 border-black bg-black px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap items-start justify-between gap-8">
              <div>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center border-4 border-white bg-[#A3E635]">
                    <BookOpen className="h-6 w-6 text-black" strokeWidth={3} />
                  </div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-[#A3E635]">文档中心</p>
                </div>
                <h1 className="mt-6 text-5xl font-black uppercase leading-none text-white lg:text-7xl">
                  CALL ANYTHING
                  <br />
                  <span style={{ color: brandPalette.teal }}>DOCS</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base font-bold leading-7 text-zinc-300">
                  从快速开始到完整协议规范，覆盖 Caller、Responder、Platform 运维与 Hotline 开发的全部文档。
                </p>
              </div>
              <div className="grid gap-2">
                <div
                  className="border-4 border-white/30 bg-white/10 p-4 text-white"
                  style={{ borderLeftColor: brandPalette.green, borderLeftWidth: 4 }}
                >
                  <Terminal className="h-5 w-5 text-[#A3E635]" strokeWidth={3} />
                  <p className="mt-2 text-xs font-black uppercase tracking-[0.18em] text-zinc-400">快速安装</p>
                  <p className="mt-1 whitespace-pre-wrap font-mono text-sm font-bold leading-6 text-white">{`npm install -g @delexec/ops
delexec-ops bootstrap --email you@example.com --text "Summarize this bootstrap request."
delexec-ops status
delexec-ops run-example --text "Summarize this follow-up request."`}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Doc layout */}
        <section className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
              {/* Sidebar */}
              <aside className="lg:sticky lg:top-28 lg:self-start">
                <SectionNav sections={DOC_SECTIONS} activeId={activeSectionId} onSelect={setActiveSectionId} />

                <div className="mt-8 border-4 border-black bg-[#F8F6EF] p-5">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-500">对象速查</p>
                  <div className="mt-4 space-y-3 text-xs font-bold">
                    {[
                      { label: "Protocol", color: brandPalette.green },
                      { label: "Caller", color: brandPalette.teal },
                      { label: "Responder", color: brandPalette.orange },
                      { label: "Hotline", color: brandPalette.pink },
                      { label: "Platform", color: brandPalette.purple }
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2.5">
                        <div className="h-2.5 w-2.5 border-2 border-black" style={{ backgroundColor: item.color }} />
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Content area */}
              <div className="min-w-0">
                {/* Section header */}
                <div
                  className="border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                  style={{ backgroundColor: "#F8F6EF", borderLeftColor: activeSection.color, borderLeftWidth: 8 }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-12 w-12 items-center justify-center border-4 border-black"
                      style={{ backgroundColor: activeSection.color }}
                    >
                      {activeSection.icon}
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-500">文档章节</p>
                      <h2 className="mt-1 text-2xl font-black uppercase">{activeSection.label}</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm font-bold leading-6 text-zinc-700">{activeSection.summary}</p>
                </div>

                {/* Article grid */}
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {activeSection.articles.map((article) => (
                    <ArticleCard key={article.id} article={article} sectionColor={activeSection.color} />
                  ))}
                </div>

                {/* Placeholder notice */}
                <div className="mt-8 border-4 border-dashed border-black bg-white/60 p-6">
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">文档开发状态</p>
                  <p className="mt-3 text-sm font-bold leading-6 text-zinc-700">
                    文档中心当前为框架版本。各章节的正文内容正在持续撰写中，预计随 v0.2 版本一同发布。
                    协议规范类文档（合约格式、Token 模型、能力模板）已有对应的
                    {" "}
                    <span className="font-black">
                      repos/protocol/docs/current/spec/
                    </span>
                    {" "}
                    机器可读版本，可直接参考。
                  </p>
                </div>

                <div className="mt-8 border-4 border-black bg-[#F8F6EF] p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                  <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: brandPalette.blue }}>
                    阅读建议
                  </p>
                  <h2 className="mt-3 text-2xl font-black uppercase text-black">怎么用这份文档</h2>
                  <div className="mt-4 grid gap-4 text-sm font-bold leading-6 text-zinc-700 md:grid-cols-3">
                    <p>
                      如果你是第一次接触 CALL ANYTHING，先不要从协议字段开始。优先进入 Quick Start 页面，把 Caller 或
                      Responder 的最小闭环跑通，再回来看字段定义，会更容易把 schema、token、result_package 串起来。
                    </p>
                    <p>
                      如果你已经在做系统接入，Docs 更适合作为查阅入口。它负责把协议、CLI、Platform API、自托管和 Hotline
                      设计组织成一套统一路径，避免你在多个仓库之间来回翻找命令和字段。
                    </p>
                    <p>
                      文档中心和 Marketplace 是配套关系。Docs 回答“怎么接”和“怎么发”，Marketplace 回答“有哪些能力可发现”。
                      两者之间通过 Hotline、Responder 和能力模板这些统一对象名保持一致。
                    </p>
                  </div>
                </div>

                <LinkClusterSection
                  eyebrow="Docs Entry"
                  title="高频入口"
                  intro="下面这几个入口覆盖了大部分搜索和接入意图，可以直接继续往下走。"
                  accent={brandPalette.blue}
                  links={[
                    {
                      href: "/docs/quick-start-caller/",
                      title: "Caller Quick Start",
                      summary: "适合 Agent 应用方，重点是 supervisor、本地调用和 result_package 读取。",
                      meta: "caller · quick start",
                    },
                    {
                      href: "/docs/quick-start-responder/",
                      title: "Responder Quick Start",
                      summary: "适合能力供给方，重点是启用 responder、注册 hotline 和 Marketplace 审核。",
                      meta: "responder · hotline publish",
                    },
                    {
                      href: "/docs/deployability-profiles/",
                      title: "部署 Profiles",
                      summary: "解释 Local Agent Loop、Selfhost Platform、Public Stack 和 Console 管理面的 ready now / planned 边界。",
                      meta: "selfhost · safety",
                    },
                    {
                      href: "/marketplace/",
                      title: "Marketplace",
                      summary: "看当前公开能力目录，理解 Docs 中的 Hotline 与 Responder 在目录里如何呈现。",
                      meta: "catalog · discovery",
                    },
                    {
                      href: "/console/",
                      title: "Console",
                      summary: "查看协议控制面的原型，理解审批、状态、路由和运行时面板。",
                      meta: "console · runtime",
                    },
                  ]}
                />

                <FaqSection
                  eyebrow="Docs FAQ"
                  title="文档常见问题"
                  intro="这些问题来自首次接入时最常见的搜索意图和团队内部沟通问题。"
                  items={ROUTE_FAQS["/docs/"]}
                  accent={brandPalette.blue}
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
